import { List } from 'immutable'
import 'intersection-observer'
import * as React from 'react'
import { InView } from 'react-intersection-observer'
import { IOWidget, Updater, Widget, fromJSX } from 'widgets-for-react'
import { blockInviewHandler } from '../../../drupal/rendering/inview_utils.mod'
import { ImageSize } from '../types.gen'
import { getClassName } from '../utils.gen'
import { ImageContainer } from './ImageContainer.gen'
import { CMSState } from '../../../publicSite/drupalPage/state'

export type ImageGalleryBlockProps = {
  index: number
  title?: string | React.ReactNode
  intro?: string
  images: ListWithAtLeast3Items<ImageGalleryImageProps>
  imagePreviewCount: number
  get_default_data_src: () => ImageSize
  get_default_data_srcset: () => [ImageSize, string][]
  copy: ImageGalleryCopy
  inview?: {
    threshold?: number
    triggerOnce?: boolean
  }
}

export type ImageGalleryImageProps = {
  index: number
  image: number
  image_size: ImageSize
  imageDataSizes?: string
  image_alt: string
  is_foreground: boolean
  caption?: string
  source?: string
}

export type ImageGalleryCopy = {
  close: string
  more: string
  enlarged: string
  previous: string
  next: string
}

export type ImageGalleryBlockStyle = Partial<{
  block_classname: string
  inview_classname: string
  inner_classname: string
  content_classname: string
  header_classname: string
  title_classname: string
  intro_classname: string
  gallery_list_classname: string
}> &
  ImageGalleryItemStyle

const ondoneImage = (onDone: (_: Updater<CMSState>) => void, props: ImageGalleryBlockProps, index: number) => (
  action: Updater<'next' | 'prev' | 'close' | 'open'>
) => {
  let openNew = action('open')
  onDone((s1) => {
    let currentBlock = s1.blocks.get(props.index)
    if (currentBlock.kind === 'imageGalleryBlock') {
      currentBlock = {
        ...currentBlock,
        value: [
          {
            ...currentBlock.value[0],
            images: new ListWithAtLeast3Items<ImageGalleryImageProps>(
              currentBlock.value[0].images.items
                .map((b, b_i) => {
                  if (openNew == 'next' && b_i - 1 == index) {
                    return { ...b, is_foreground: true }
                  }
                  if (openNew == 'prev' && b_i + 1 == index) {
                    return { ...b, is_foreground: true }
                  }
                  if (openNew == 'open' && b_i == index) {
                    return { ...b, is_foreground: true }
                  }
                  return { ...b, is_foreground: false }
                })
                .toList()
            )
          },
          currentBlock.value[1]
        ]
      }
    }
    return { ...s1, blocks: s1.blocks.set(props.index, currentBlock) }
  })
}

export function ImageGalleryBlock(
  props: ImageGalleryBlockProps,
  options?: { style?: ImageGalleryBlockStyle; key?: string }
): IOWidget<CMSState, Updater<CMSState>> {
  return (s: CMSState) =>
    fromJSX((onDone) => {
      let images = props.images.GetFirst(props.imagePreviewCount)
      let openImage = props.images.items.find((i) => i.is_foreground)
      let openImageIndex = props.images.items.findIndex((i) => i.is_foreground)

      return (
        <InView
          as="section"
          onChange={(inView) =>
            blockInviewHandler(
              'imageGallery',
              inView,
              `io-${options && options.key ? options.key : props.index}`,
              getClassName(options, 'inview_classname', 'inview')
            )
          }
          triggerOnce={props.inview && props.inview.triggerOnce ? props.inview.triggerOnce : false}
          threshold={props.inview && props.inview.threshold ? props.inview.threshold : 0.1}
          id={`io-${options && options.key ? options.key : props.index}`}
          className={getClassName(options, 'block_classname', 'container')}
          key={options && options.key ? options.key : `block-${props.index}`}
        >
          <div className={getClassName(options, 'inner_classname', 'py-5')}>
            {(props.title || props.intro) && (
              <header className={options && options.style ? options.style.header_classname : ''}>
                {props.title && <h2 className={getClassName(options, 'title_classname', 'mb-3')}>{props.title}</h2>}
                {props.intro && <p className={getClassName(options, 'intro_classname', '')}>{props.intro}</p>}
              </header>
            )}

            <div className={getClassName(options, 'content_classname', '')}>
              <ul className={getClassName(options, 'gallery_list_classname', 'list-unstyled mb-0 row')}>
                {images.items
                  .map((value, index) =>
                    ImageGalleryItem(
                      s,
                      {
                        index: props.index,
                        is_first: index == 0,
                        is_last: index + 1 >= props.images.items.count(),
                        id: value.image,
                        image: value.image_size,
                        image_alt: value.image_alt,
                        key: value.image + `${index}`,
                        get_default_data_src: props.get_default_data_src,
                        get_default_data_srcset: props.get_default_data_srcset,
                        additional_info:
                          index + 1 >= images.items.count() && images.remaining_items > 0
                            ? `+${images.remaining_items}`
                            : undefined,
                        caption: value.caption,
                        source: value.source,
                        copy: props.copy,
                        isOpen: value.is_foreground,
                        imageDataSizes: value.imageDataSizes
                      },
                      index,
                      options
                    ).run(ondoneImage(onDone, props, index))
                  )
                  .toArray()}
              </ul>
            </div>

            {openImage &&
              ImageGalleryOverlay(
                s,
                {
                  index: props.index,
                  is_first: openImageIndex == 0,
                  is_last: openImageIndex + 1 >= props.images.items.count(),
                  id: openImage.image,
                  image: openImage.image_size,
                  image_alt: openImage.image_alt,
                  key: openImage.image + `${openImageIndex}`,
                  get_default_data_src: props.get_default_data_src,
                  get_default_data_srcset: props.get_default_data_srcset,
                  additional_info:
                    openImageIndex + 1 >= images.items.count() && images.remaining_items > 0
                      ? `+${images.remaining_items}`
                      : undefined,
                  caption: openImage.caption,
                  source: openImage.source,
                  copy: props.copy,
                  isOpen: openImage.is_foreground,
                  imageDataSizes: openImage.imageDataSizes
                },
                openImageIndex,
                options
              ).run(ondoneImage(onDone, props, openImageIndex))}
          </div>
        </InView>
      )
    })
}

export type ImageGalleryItemProps = {
  index: number
  is_first: boolean
  is_last: boolean
  key?: string
  id: number
  image: ImageSize
  image_alt: string
  imageDataSizes?: string
  get_default_data_src: () => ImageSize
  get_default_data_srcset: () => [ImageSize, string][]
  additional_info?: string
  caption?: string
  source?: string
  copy: ImageGalleryCopy
  isOpen: boolean
}

export type ImageGalleryItemStyle = Partial<{
  gallery_item_classname: string
  gallery_button_classname: string
  gallery_image_classname: string
  gallery_toggle_classname: string
  lightbox_classname: string
  lightbox_inner_classname: string
  lightbox_figure_classname: string
  lightbox_visual_classname: string
  lightbox_image_classname: string
  lightbox_figcaption_classname: string
  lightbox_caption_classname: string
  lightbox_source_classname: string
  lightbox_nav_classname: string
  lightbox_nav_inner_classname: string
  lightbox_controls_classname: string
  lightbox_controls_inner_classname: string
  lightbox_next_classname: string
  lightbox_prev_classname: string
  lightbox_close_classname: string
  icon_close_id: string
  icon_previous_id: string
  icon_next_id: string
}>

function ImageGalleryItem(
  cmsState: CMSState,
  props: ImageGalleryItemProps,
  index: number,
  options?: { style?: ImageGalleryItemStyle; key?: string }
): Widget<Updater<'next' | 'prev' | 'close' | 'open'>> {
  return fromJSX((onDone) => {
    return (
      <li
        className={getClassName(options, 'gallery_item_classname', 'col-sm-6 col-md-4 pb-3')}
        key={`image-${options && options.key ? options.key : props.index}-${index}`}
      >
        <button
          onClick={(e) => {
            onDone((_) => 'open')
            e.stopPropagation()
            document.documentElement.classList.add('lightbox--open')
          }}
          className={
            getClassName(options, 'gallery_button_classname', 'btn p-0 w-100') +
            (props.additional_info ? ' has-toggle' : '')
          }
          aria-expanded={props.isOpen}
          aria-controls={`a11y-lightbox-${props.id}`}
        >
          {props.isOpen}
          {cmsState.images.has(props.id) &&
            ImageContainer(
              cmsState.images.get(props.id),
              props.image,
              getClassName(options, 'gallery_image_classname', 'w-100'),
              props.image_alt,
              props.get_default_data_src,
              props.get_default_data_srcset
            ).run((_) => {})}
          {props.additional_info ? (
            <div className={getClassName(options, 'gallery_toggle_classname', '')}>
              {props.additional_info}
              <span className="sr-text">{props.copy.more}</span>
            </div>
          ) : (
            <span className="sr-text">{props.copy.enlarged}</span>
          )}
        </button>
      </li>
    )
  })
}

function ImageGalleryOverlay(
  cmsState: CMSState,
  props: ImageGalleryItemProps,
  index: number,
  options?: { style?: ImageGalleryItemStyle; key?: string }
): Widget<Updater<'next' | 'prev' | 'close' | 'open'>> {
  return fromJSX((onDone) => {
    return (
      <div
        id={`a11y-lightbox-${props.id}`}
        aria-hidden="true"
        className={getClassName(options, 'lightbox_classname', 'mt-3 p-5 bg-light')}
        onClick={(e) => {
          onDone((_) => 'close')
          e.stopPropagation()
          document.documentElement.classList.remove('lightbox--open')
        }}
        key={`overlay-${options && options.key ? options.key : props.index}-${index}`}
      >
        <div className={getClassName(options, 'lightbox_inner_classname', '')}>
          <div className={getClassName(options, 'lightbox_nav_classname', '')}>
            <div className={getClassName(options, 'lightbox_nav_inner_classname', '')}>
              <button
                onClick={(e) => {
                  onDone((_) => 'close')
                  e.stopPropagation()
                  document.documentElement.classList.remove('lightbox--open')
                }}
                className={getClassName(options, 'lightbox_close_classname', 'btn btn-outline-dark d-block ml-auto')}
              >
                <span className="sr-text">{props.copy.close}</span>
                <img src="/images/icons/icon--closing-x-white.svg" alt="" width="24" height="24" />
              </button>
            </div>
          </div>

          <figure className={getClassName(options, 'lightbox_figure_classname', 'mt-3')}>
            <div className={getClassName(options, 'lightbox_visual_classname', '')}>
              {cmsState.images.has(props.id) &&
                ImageContainer(
                  cmsState.images.get(props.id),
                  props.image,
                  getClassName(options, 'lightbox_image_classname', 'w-100'),
                  props.image_alt,
                  props.get_default_data_src,
                  props.get_default_data_srcset,
                  props.imageDataSizes
                ).run((_) => {})}
              {(props.caption || props.source) && (
                <figcaption className={getClassName(options, 'lightbox_figcaption_classname', 'mt-2 text-center')}>
                  {props.caption ? (
                    <p className={getClassName(options, 'lightbox_caption_classname', 'm-0 small text-secondary')}>
                      {props.caption}
                    </p>
                  ) : (
                    <></>
                  )}
                  {props.source ? (
                    <p className={getClassName(options, 'lightbox_source_classname', 'm-0 small text-secondary')}>
                      {props.source}
                    </p>
                  ) : (
                    <></>
                  )}
                </figcaption>
              )}
            </div>
          </figure>

          <div className={getClassName(options, 'lightbox_controls_classname', '')}>
            <div
              className={getClassName(options, 'lightbox_controls_inner_classname', 'd-flex justify-content-between')}
            >
              {!props.is_first ? (
                <button
                  onClick={(e) => {
                    onDone((_) => 'prev')
                    e.stopPropagation()
                  }}
                  className={getClassName(options, 'lightbox_prev_classname', 'btn btn-dark')}
                >
                  <span className="sr-text">{props.copy.previous}</span>
                  <img src="/images/icons/arrow--left--white.svg" alt="" width="24" height="24" />
                </button>
              ) : (
                ''
              )}
              {!props.is_last ? (
                <button
                  onClick={(e) => {
                    onDone((_) => 'next')
                    e.stopPropagation()
                  }}
                  className={getClassName(options, 'lightbox_next_classname', 'btn btn-dark ml-auto')}
                >
                  <span className="sr-text">{props.copy.next}</span>
                  <img src="/images/icons/arrow--right--white.svg" alt="" width="24" height="24" />
                </button>
              ) : (
                ''
              )}
            </div>
          </div>
        </div>
      </div>
    )
  })
}

export class ListWithAtLeast3Items<T> {
  public items: List<T>
  public constructor(images: List<T>) {
    if (images.count() < 3) {
      throw `ListWithAtLeast3Items: expected at least 3 items, but received ${images.count()}`
    }
    this.items = images
  }
  public GetFirst(n: number): { items: List<T>; remaining_items: number } {
    if (this.items.count() <= n) return { items: this.items, remaining_items: 0 }
    let selected_images = this.items.take(n).toList()
    return { items: selected_images, remaining_items: this.items.count() - selected_images.count() }
  }
}
